import { useState } from 'react'
import {
  adminApproveApplication,
  adminListApplications,
  adminRejectApplication,
  type StaffApplicationAdmin,
} from '../../api/auth'
import { Button } from '../../components/ui/Button'
import { Card, ErrorBox } from '../../components/ui/Card'
import { useFetch } from '../../hooks/useFetch'
import {
  emptyStateClass,
  pageHeaderClass,
  pageTitleClass,
  tdClass,
  thClass,
  tbodyHoverClass,
} from '@/lib/tableStyles'
import { useToast } from '../../components/ui/Toast'

const ROLE_LABELS: Record<string, string> = {
  veterinarian: 'Veterinarian',
  vet_technician: 'Veterinary Technician',
  receptionist: 'Receptionist',
  non_clinical: 'Non-Clinical',
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export default function StaffApplicationList() {
  const toast = useToast()
  const { data, loading, error, refetch } = useFetch(() => adminListApplications(), [])
  const [actionError, setActionError] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<number | null>(null)

  const handleApprove = async (app: StaffApplicationAdmin) => {
    setProcessingId(app.id)
    setActionError(null)
    try {
      await adminApproveApplication(app.id)
      toast.success(`Approved ${app.user_name ?? app.user_email} as ${ROLE_LABELS[app.clinical_role] ?? app.clinical_role}`)
      refetch()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to approve')
    } finally {
      setProcessingId(null)
    }
  }

  const handleReject = async (app: StaffApplicationAdmin) => {
    setProcessingId(app.id)
    setActionError(null)
    try {
      await adminRejectApplication(app.id)
      toast.success('Application rejected')
      refetch()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to reject')
    } finally {
      setProcessingId(null)
    }
  }

  const applications = data ?? []

  return (
    <div className="max-w-[960px] mx-auto px-4 py-8">
      <div className={pageHeaderClass}>
        <h1 className={pageTitleClass}>Staff Applications</h1>
      </div>

      <p className="text-sm text-text-3 mb-6">
        Pending applications from users who have applied to join a practice as a veterinarian.
        Receptionist, technician, and non-clinical roles are auto-approved and won't appear here.
      </p>

      {(error || actionError) && <ErrorBox>{error ?? actionError}</ErrorBox>}

      <Card className="overflow-hidden p-0">
        {loading ? (
          <p className="text-sm text-text-3 p-6">Loading…</p>
        ) : applications.length === 0 ? (
          <p className={emptyStateClass}>No pending applications.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Applicant</th>
                <th className={thClass}>Practice</th>
                <th className={thClass}>Role</th>
                <th className={thClass}>Applied</th>
                <th className={thClass} />
              </tr>
            </thead>
            <tbody className={tbodyHoverClass}>
              {applications.map(app => (
                <tr key={app.id}>
                  <td className={tdClass}>
                    <div className="font-medium text-text-heading">{app.user_name ?? '—'}</div>
                    <div className="text-text-3 text-xs">{app.user_email}</div>
                  </td>
                  <td className={tdClass}>{app.practice_name}</td>
                  <td className={tdClass}>{ROLE_LABELS[app.clinical_role] ?? app.clinical_role}</td>
                  <td className={tdClass}>{formatDate(app.created_at)}</td>
                  <td className={tdClass}>
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={processingId === app.id}
                        onClick={() => void handleApprove(app)}
                      >
                        {processingId === app.id ? '…' : 'Approve'}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={processingId === app.id}
                        onClick={() => void handleReject(app)}
                      >
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}
